/**
 * PLAY YOUR GAME™ — Template maestro de mapas premium (Fase 1).
 *
 * Ruta: /mapa/:dominant/:secondary  (ej. /mapa/halcon/lobo)
 * NO está enlazada desde la landing pública ni desde admin.
 *
 * Render 100% dirigido por `MAPAS[dominant_secondary]` en data/mapas.js.
 * El sistema visual (header, sidebar, 11 secciones, slider, paleta) es fijo.
 *
 * Para añadir un nuevo mapa (ej. LEÓN+HALCÓN): añadir entrada en data/mapas.js.
 * Este componente NO necesita cambios.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowDown } from "lucide-react";
import { getMapa, MAPA_GLOBAL } from "@/data/mapas";

const SECTIONS = [
  { id: "portada", label: "00. Portada", color: "green" },
  { id: "tension1", label: "01. Espejo", color: "green" },
  { id: "arquetipo", label: "02. Tus Fuerzas", color: "green" },
  { id: "elcamino", label: "03. El Camino", color: "green" },
  { id: "tension2", label: "04. Tensión", color: "green" },
  { id: "silenciosombra", label: "05. Advertencia", color: "red" },
  { id: "problemasombra", label: "06. El Límite", color: "red" },
  { id: "modosombra", label: "07. Modo Sombra", color: "red" },
  { id: "tension3", label: "08. Intención", color: "green" },
  { id: "regulacion", label: "09. Regulación", color: "green" },
  { id: "cierre", label: "10. Evolución", color: "green" },
];

function scrollToSection(id) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
}

export default function MapaPage() {
  const { dominant, secondary } = useParams();
  const cfg = useMemo(() => getMapa(dominant, secondary), [dominant, secondary]);
  const [active, setActive] = useState("portada");
  const [tension, setTension] = useState(50);

  // Sidebar scroll-spy (replicates the inline script of the source HTML).
  useEffect(() => {
    const onScroll = () => {
      const mid = window.innerHeight / 2;
      for (const s of SECTIONS) {
        const el = document.getElementById(s.id);
        if (!el) continue;
        const r = el.getBoundingClientRect();
        if (r.top <= mid && r.bottom >= mid) {
          setActive(s.id);
          break;
        }
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Inject font + minimal extra CSS only for this page (keeps the rest of the app untouched).
  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap";
    document.head.appendChild(link);
    const style = document.createElement("style");
    style.dataset.mapa = "1";
    style.textContent = `
      .mapa-root{font-family:'Inter',ui-sans-serif,system-ui,sans-serif;}
      .mapa-root ::-webkit-scrollbar{width:6px}
      .mapa-root ::-webkit-scrollbar-track{background:#0B0B0C}
      .mapa-root ::-webkit-scrollbar-thumb{background:#29BC9B;border-radius:3px}
      .mapa-text-glow{text-shadow:0 0 12px rgba(41,188,155,.2)}
      .mapa-text-glow-red{text-shadow:0 0 12px rgba(142,47,53,.4)}
      .mapa-fade-in{animation:mapaFadeIn .8s cubic-bezier(.16,1,.3,1) forwards}
      @keyframes mapaFadeIn{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
    `;
    document.head.appendChild(style);
    return () => {
      link.remove();
      style.remove();
    };
  }, []);

  if (!cfg) return <MapaNotFound dominant={dominant} secondary={secondary} />;

  const sliderState =
    cfg.regulacion.slider.states.find((s) => tension <= s.max) ||
    cfg.regulacion.slider.states[cfg.regulacion.slider.states.length - 1];

  return (
    <div
      className="mapa-root bg-brandDark text-brandWhite antialiased overflow-x-hidden selection:bg-brandGreen selection:text-brandDark"
      data-testid="mapa-page"
    >
      {/* HEADER */}
      <header className="fixed top-0 left-0 w-full z-50 bg-brandDark/80 backdrop-blur-md border-b border-brandWhite/5 px-6 py-4 md:px-12 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <span className="text-[10px] uppercase font-bold tracking-[0.3em] text-brandGreen mapa-text-glow">
            {MAPA_GLOBAL.brand}
          </span>
        </div>
        <div className="flex items-center gap-6">
          <span className="hidden md:inline-block text-[11px] text-brandGrayText font-mono uppercase tracking-wider">
            {MAPA_GLOBAL.headerTagline}
          </span>
          <div className="h-3 w-[1px] bg-brandWhite/10 hidden md:block"></div>
          <span className="text-[11px] font-medium tracking-widest uppercase">
            {cfg.dominant} + {cfg.secondary}
          </span>
        </div>
      </header>

      {/* SIDEBAR */}
      <aside className="fixed left-6 top-1/2 -translate-y-1/2 z-40 hidden xl:flex flex-col gap-4">
        {SECTIONS.map((s) => {
          const isActive = active === s.id;
          const dot =
            isActive && s.color === "red"
              ? "bg-brandRedSombra scale-125"
              : isActive
              ? "bg-brandGreen scale-125"
              : "bg-brandWhite/20";
          const labelClr = s.color === "red" ? "text-brandRedSombra font-bold" : "";
          return (
            <button
              key={s.id}
              onClick={() => scrollToSection(s.id)}
              className="group flex items-center gap-3 text-left"
              data-testid={`mapa-nav-${s.id}`}
            >
              <span
                className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${dot} group-hover:bg-brandGreen`}
              ></span>
              <span
                className={`text-[10px] text-brandGrayText group-hover:text-brandWhite opacity-0 group-hover:opacity-100 transition-all duration-300 uppercase font-mono tracking-widest ${labelClr}`}
              >
                {s.label}
              </span>
            </button>
          );
        })}
      </aside>

      <main className="w-full flex flex-col items-center">
        {/* 00 PORTADA */}
        <section
          id="portada"
          className="w-full min-h-screen bg-brandDark flex flex-col justify-between px-6 py-24 md:p-32 relative overflow-hidden"
        >
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(41,188,155,0.06),transparent_60%)] pointer-events-none"></div>
          <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[1px] h-40 bg-gradient-to-b from-brandGreen/40 to-transparent"></div>

          <div className="w-full flex justify-between items-start z-10">
            <div>
              <p className="text-[11px] tracking-widest uppercase text-brandGreen font-bold font-mono">
                {cfg.portada.kicker}
              </p>
              <div className="h-[2px] w-12 bg-brandGreen mt-2"></div>
            </div>
            <p className="text-[11px] tracking-widest uppercase text-brandGrayText font-mono font-bold">
              {cfg.portada.kickerRight}
            </p>
          </div>

          <div className="my-auto z-10 text-center max-w-5xl mx-auto flex flex-col justify-center items-center">
            <h1 className="text-6xl md:text-8xl font-light tracking-wide text-brandWhite mb-4 select-none">
              {cfg.dominant} <span className="text-brandGreen mapa-text-glow font-bold">+</span> {cfg.secondary}
            </h1>
            <div className="flex items-center gap-4 mb-12">
              <span className="w-8 h-[1px] bg-brandGrayText/50"></span>
              <p className="text-[12px] md:text-sm tracking-[0.25em] uppercase text-brandGrayText font-light">
                {MAPA_GLOBAL.heroFixedLine}
              </p>
              <span className="w-8 h-[1px] bg-brandGrayText/50"></span>
            </div>
            <blockquote className="text-lg md:text-3xl text-brandWhite/90 font-light max-w-3xl leading-relaxed tracking-wide italic px-4 border-l border-brandGreen/40 whitespace-pre-line">
              {cfg.portada.quote}
            </blockquote>
          </div>

          <div className="w-full flex flex-col md:flex-row justify-between items-center gap-4 z-10 border-t border-brandWhite/10 pt-8">
            <p className="text-[11px] text-brandGrayText tracking-wider font-mono whitespace-pre-line">
              {cfg.portada.footerLeft}
            </p>
            <button
              onClick={() => scrollToSection("tension1")}
              className="flex items-center gap-2 cursor-pointer group"
            >
              <span className="text-[11px] text-brandGreen group-hover:text-brandWhite tracking-widest uppercase transition-colors duration-300 font-mono font-bold">
                {cfg.portada.footerRight}
              </span>
              <ArrowDown className="w-4 h-4 text-brandGreen group-hover:translate-y-1 transition-all duration-300" />
            </button>
          </div>
        </section>

        {/* 01 TENSIÓN 1 */}
        <TensionPage
          id="tension1"
          kicker={cfg.tension1.kicker}
          kickerColor="text-brandGreen"
          frase={cfg.tension1.frase}
          ruleColor="bg-brandGrayText/40"
          radial="from-top-right-green"
        />

        {/* 02 ARQUETIPO — TUS FUERZAS */}
        <section
          id="arquetipo"
          className="w-full bg-brandGrayLight text-brandTextPrimary px-6 py-24 md:py-32 md:px-24 flex justify-center"
        >
          <div className="max-w-4xl w-full space-y-16">
            <div className="border-b border-brandTextPrimary/10 pb-8 flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
              <div>
                <span className="text-[11px] tracking-widest text-brandGreen font-bold font-mono uppercase">
                  {cfg.arquetipo.kicker}
                </span>
                <h3 className="text-4xl font-bold tracking-tight mt-2 text-brandDark">
                  {cfg.arquetipo.titulo}
                </h3>
              </div>
              <p className="text-[12px] text-brandGrayText font-mono uppercase tracking-widest">
                {cfg.arquetipo.rightLabel}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16">
              {[cfg.arquetipo.dominante, cfg.arquetipo.secundario].map((f, i) => (
                <div key={i} className="space-y-6">
                  <div className="flex items-center gap-3">
                    <span className="text-[12px] font-mono text-brandGreen font-bold bg-brandDark text-brandWhite px-2.5 py-0.5 rounded">
                      {f.numero}
                    </span>
                    <h4 className="text-lg font-bold tracking-tight uppercase">
                      {f.titulo}
                    </h4>
                  </div>
                  <div className="space-y-4 text-brandGrayText text-sm leading-relaxed text-justify">
                    <p className="text-brandTextPrimary font-medium text-base whitespace-pre-line">{f.line}</p>
                    <p className="whitespace-pre-line">{f.body}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-brandWhite border border-brandTextPrimary/5 p-8 md:p-12 rounded-xl space-y-4 shadow-sm">
              <span className="text-[10px] tracking-widest text-brandGreen font-bold font-mono uppercase">
                {cfg.arquetipo.ventaja.kicker}
              </span>
              <h4 className="text-2xl font-light leading-tight text-brandDark">
                {cfg.arquetipo.ventaja.frase}
              </h4>
              <p className="text-brandGrayText text-sm leading-relaxed whitespace-pre-line">
                {cfg.arquetipo.ventaja.body}
              </p>
            </div>
          </div>
        </section>

        {/* 03 EL CAMINO */}
        <section
          id="elcamino"
          className="w-full bg-brandWhite text-brandTextPrimary px-6 py-24 md:py-32 md:px-24 flex justify-center border-t border-brandGrayLight"
        >
          <div className="max-w-4xl w-full space-y-16">
            <div className="text-center max-w-2xl mx-auto space-y-4">
              <span className="text-[10px] tracking-[0.3em] text-brandGreen font-bold uppercase font-mono">
                {cfg.elcamino.kicker}
              </span>
              <h3 className="text-3xl md:text-4xl font-light text-brandDark tracking-tight">
                {cfg.elcamino.titulo}
              </h3>
              <p className="text-brandGrayText text-sm">{cfg.elcamino.subtitle}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-4">
              {cfg.elcamino.etapas.map((e, i) => {
                const variantClasses =
                  e.variant === "sombra"
                    ? "border-brandRedSombra/10 bg-brandRedSombraDark/5 hover:border-brandRedSombra/30"
                    : e.variant === "green"
                    ? "border-brandGreen/10 bg-brandGreen/5 hover:border-brandGreen/30"
                    : "border-brandGrayLight bg-brandGrayLight/50 hover:border-brandGreen/20";
                const numColor =
                  e.variant === "sombra"
                    ? "text-brandRedSombra"
                    : e.variant === "green"
                    ? "text-brandGreen"
                    : "text-brandGrayText";
                const titleColor =
                  e.variant === "sombra"
                    ? "text-brandRedSombra"
                    : e.variant === "green"
                    ? "text-brandGreen"
                    : "text-brandDark";
                return (
                  <div
                    key={i}
                    className={`border rounded-xl p-8 space-y-4 flex flex-col justify-between transition-all ${variantClasses}`}
                  >
                    <div className="space-y-4">
                      <span
                        className={`text-[11px] font-mono font-bold uppercase tracking-widest ${numColor}`}
                      >
                        {e.numero}
                      </span>
                      <h4 className={`text-xl font-bold ${titleColor}`}>{e.titulo}</h4>
                      <p className="text-brandGrayText text-sm leading-relaxed whitespace-pre-line">{e.body}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* 04 TENSIÓN 2 */}
        <TensionPage
          id="tension2"
          kicker={cfg.tension2.kicker}
          kickerColor="text-brandRedSombra"
          frase={cfg.tension2.frase}
          ruleColor="bg-brandRedSombra"
          radial="from-bottom-left-red"
        />

        {/* 05 SILENCIO SOMBRA */}
        <section
          id="silenciosombra"
          className="w-full min-h-screen bg-brandDark flex flex-col justify-center items-center px-6 md:px-24 py-32 relative overflow-hidden"
        >
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-brandRedSombraDark/60 to-transparent pointer-events-none"></div>
          <div className="max-w-3xl text-center space-y-6 z-10">
            <span className="text-[10px] tracking-[0.4em] text-brandRedSombra font-mono font-bold uppercase">
              {cfg.silencio.kicker}
            </span>
            <h3 className="text-4xl md:text-6xl font-extralight text-brandWhite tracking-tight">
              {cfg.silencio.line1} <br className="md:block hidden" />
              <span className="text-brandRedSombra mapa-text-glow-red font-semibold">
                {cfg.silencio.line2Highlight}
              </span>
            </h3>
          </div>
        </section>

        {/* 06 PROBLEMA SOMBRA */}
        <section
          id="problemasombra"
          className="w-full min-h-screen bg-brandDark flex flex-col justify-center items-center px-6 md:px-24 py-32 relative overflow-hidden"
        >
          <div className="max-w-3xl text-center space-y-4 z-10">
            <h3 className="text-2xl md:text-4xl font-extralight text-brandGrayText tracking-tight whitespace-pre-line">
              {cfg.problema.frase}
            </h3>
            <p className="text-brandRedSombra mapa-text-glow-red text-sm font-mono tracking-widest pt-4 uppercase">
              {cfg.problema.footer}
            </p>
          </div>
        </section>

        {/* 07 MODO SOMBRA */}
        <section
          id="modosombra"
          className="w-full min-h-screen bg-brandDark text-brandWhite px-6 py-24 md:py-32 md:px-24 flex flex-col justify-center relative overflow-hidden"
        >
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(142,47,53,0.12),transparent_70%)] pointer-events-none"></div>
          <div className="max-w-4xl w-full mx-auto space-y-16 z-10">
            <div className="border-b border-brandRedSombra pb-8 flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
              <div>
                <span className="text-[11px] tracking-widest text-brandRedSombra font-bold font-mono uppercase">
                  {cfg.modosombra.kicker}
                </span>
                <h3 className="text-4xl font-bold tracking-wider mt-2 text-brandWhite uppercase">
                  {cfg.modosombra.titulo}
                </h3>
              </div>
              <p className="text-[12px] text-brandRedSombra font-mono uppercase tracking-widest font-bold mapa-text-glow-red">
                {cfg.modosombra.rightLabel}
              </p>
            </div>

            <div className="space-y-4 max-w-3xl">
              <h4 className="text-2xl md:text-4xl font-light text-brandWhite leading-relaxed whitespace-pre-line">
                {cfg.modosombra.quote}
              </h4>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {cfg.modosombra.bloques.map((b, i) => (
                <div
                  key={i}
                  className="bg-brandRedSombraDark/40 border border-brandRedSombra/30 rounded-xl p-8 space-y-4 hover:border-brandRedSombra transition-all"
                >
                  <span className="text-[10px] font-mono text-brandRedSombra font-bold uppercase tracking-widest">
                    {b.tag}
                  </span>
                  <h5 className="text-xl font-bold text-brandWhite uppercase">{b.titulo}</h5>
                  <p className="text-brandWhite/90 text-sm leading-relaxed italic border-l border-brandRedSombra/40 pl-3">
                    {b.quote}
                  </p>
                  <p className="text-brandGrayText text-xs leading-relaxed whitespace-pre-line">{b.body}</p>
                </div>
              ))}
            </div>

            <div className="border-l-2 border-brandRedSombra pl-6 py-1">
              <p className="text-xs font-mono uppercase text-brandRedSombra mb-1">
                {cfg.modosombra.resultado.kicker}
              </p>
              <p className="text-brandGrayText text-sm leading-relaxed whitespace-pre-line">
                {cfg.modosombra.resultado.body}
              </p>
            </div>
          </div>
        </section>

        {/* 08 TENSIÓN 3 */}
        <TensionPage
          id="tension3"
          kicker={cfg.tension3.kicker}
          kickerColor="text-brandGreen"
          frase={cfg.tension3.frase}
          ruleColor="bg-brandGrayText/40"
          radial="from-center-green"
        />

        {/* 09 REGULACIÓN */}
        <section
          id="regulacion"
          className="w-full bg-brandGrayLight text-brandTextPrimary px-6 py-24 md:py-32 md:px-24 flex justify-center"
        >
          <div className="max-w-4xl w-full space-y-16">
            <div className="border-b border-brandTextPrimary/10 pb-8 flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
              <div>
                <span className="text-[11px] tracking-widest text-brandGreen font-bold font-mono uppercase">
                  {cfg.regulacion.kicker}
                </span>
                <h3 className="text-4xl font-bold tracking-tight mt-2 text-brandDark">
                  {cfg.regulacion.titulo}
                </h3>
              </div>
              <p className="text-[12px] text-brandGrayText font-mono uppercase tracking-widest font-bold">
                {cfg.regulacion.rightLabel}
              </p>
            </div>

            <p className="text-brandGrayText text-base leading-relaxed max-w-3xl whitespace-pre-line">
              {cfg.regulacion.intro}
            </p>

            <div className="space-y-6 pt-2">
              {cfg.regulacion.bloques.map((b, i) => (
                <div
                  key={i}
                  className="bg-brandWhite rounded-xl p-8 border border-brandTextPrimary/5 space-y-3 hover:shadow-md transition-shadow"
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 border-b border-brandGrayLight pb-4">
                    <div className="flex items-center gap-3">
                      <span className="text-[11px] font-mono bg-brandDark text-brandWhite px-2.5 py-1 rounded font-bold">
                        {b.numero}
                      </span>
                      <h4 className="text-lg font-bold tracking-tight text-brandDark uppercase">
                        {b.titulo}
                      </h4>
                    </div>
                    <span className="text-[11px] text-brandGreen font-semibold font-mono uppercase">
                      {b.badge}
                    </span>
                  </div>
                  <p className="text-brandGrayText text-sm leading-relaxed whitespace-pre-line">{b.body}</p>
                </div>
              ))}
            </div>

            {/* SLIDER INTERACTIVO */}
            <div className="bg-brandDark text-brandWhite rounded-xl p-8 md:p-16 space-y-12 relative overflow-hidden border border-brandWhite/10">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(41,188,155,0.08),transparent_50%)] pointer-events-none"></div>
              <div className="space-y-4 relative z-10">
                <span className="text-[10px] tracking-[0.25em] text-brandGreen font-bold font-mono uppercase">
                  {cfg.regulacion.slider.kicker}
                </span>
                <h4 className="text-3xl md:text-5xl font-light tracking-tight mt-2">
                  {cfg.regulacion.slider.titulo}
                </h4>
                <p className="text-brandGrayText text-sm font-light">
                  {cfg.regulacion.slider.subtitle}
                </p>
              </div>

              <div className="space-y-12 relative z-10">
                <div className="relative w-full py-4">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={tension}
                    onChange={(e) => setTension(Number(e.target.value))}
                    data-testid="mapa-slider"
                    className="w-full h-2 bg-brandWhite/10 rounded-lg appearance-none cursor-pointer accent-brandGreen focus:outline-none"
                  />
                  <div className="flex justify-between text-[10px] md:text-[11px] font-mono tracking-wider text-brandGrayText mt-4 uppercase">
                    <span className="text-left font-bold block max-w-[120px] md:max-w-none">
                      {cfg.regulacion.slider.leftLabel}
                    </span>
                    <span className="text-center font-bold">
                      {cfg.regulacion.slider.centerLabel}
                    </span>
                    <span className="text-right font-bold block max-w-[120px] md:max-w-none">
                      {cfg.regulacion.slider.rightLabel}
                    </span>
                  </div>
                </div>

                <div
                  className="bg-brandWhite/[0.02] border border-brandWhite/10 p-8 md:p-10 rounded-xl transition-all duration-300"
                  data-testid="mapa-slider-state"
                >
                  <p className="text-[11px] text-brandGreen font-mono uppercase tracking-widest mb-2 font-bold">
                    {sliderState.titulo}
                  </p>
                  <p className="text-lg md:text-xl text-brandWhite/90 leading-relaxed font-light whitespace-pre-line">
                    {sliderState.texto}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 10 CIERRE */}
        <section
          id="cierre"
          className="w-full min-h-screen bg-brandDark flex flex-col justify-between px-6 py-24 md:p-32 relative overflow-hidden"
        >
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,rgba(41,188,155,0.04),transparent_60%)] pointer-events-none"></div>

          <div className="w-full text-center z-10">
            <span className="text-[10px] tracking-[0.4em] uppercase text-brandGrayText font-mono font-bold">
              {MAPA_GLOBAL.brand}
            </span>
            <div className="w-8 h-[1px] bg-brandGreen/40 mx-auto mt-3"></div>
          </div>

          <div className="my-auto z-10 text-center max-w-4xl mx-auto space-y-12">
            <div className="space-y-4">
              <span className="text-[11px] tracking-widest text-brandGreen font-bold font-mono uppercase">
                {cfg.cierre.kicker}
              </span>
              <h2 className="text-4xl md:text-7xl font-extralight text-brandWhite leading-tight tracking-wide">
                {cfg.cierre.titulo} <br className="md:block hidden" />
                <span className="font-bold text-brandGreen mapa-text-glow">
                  {cfg.cierre.highlight}
                </span>
              </h2>
            </div>
            <p className="text-brandGrayText text-lg md:text-2xl font-light max-w-2xl mx-auto leading-relaxed whitespace-pre-line">
              {cfg.cierre.subtitle}
            </p>
          </div>

          <div className="w-full flex flex-col md:flex-row justify-between items-center gap-6 z-10 border-t border-brandWhite/10 pt-8 text-[11px] font-mono tracking-wider text-brandGrayText">
            <p>{MAPA_GLOBAL.cierreFooter.copyright}</p>
            <div className="flex gap-6">
              <button
                onClick={() => scrollToSection("portada")}
                className="hover:text-brandGreen transition-colors duration-300 uppercase font-bold"
              >
                {MAPA_GLOBAL.cierreFooter.backToTop}
              </button>
              <span className="text-brandWhite/20">|</span>
              <p className="uppercase">{MAPA_GLOBAL.cierreFooter.systemTagline}</p>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

/** Páginas de "tensión" (3 separadores cinematográficos negros). */
function TensionPage({ id, kicker, kickerColor, frase, ruleColor, radial }) {
  const radialBg =
    radial === "from-bottom-left-red"
      ? "bg-[radial-gradient(circle_at_bottom_left,rgba(142,47,53,0.05),transparent_40%)]"
      : radial === "from-center-green"
      ? "bg-[radial-gradient(circle_at_center,rgba(41,188,155,0.04),transparent_50%)]"
      : "bg-[radial-gradient(circle_at_top_right,rgba(41,188,155,0.03),transparent_40%)]";
  return (
    <section
      id={id}
      className="w-full min-h-screen bg-brandDark flex flex-col justify-center items-center px-6 md:px-24 py-32 relative"
    >
      <div className={`absolute inset-0 pointer-events-none ${radialBg}`}></div>
      <div className="max-w-4xl text-center space-y-8 mapa-fade-in">
        <span
          className={`text-[10px] tracking-[0.3em] font-bold uppercase font-mono ${kickerColor}`}
        >
          {kicker}
        </span>
        <h2 className="text-3xl md:text-5xl font-light text-brandWhite leading-tight tracking-wide whitespace-pre-line">
          {frase}
        </h2>
        <div className="pt-6">
          <div className={`w-12 h-[1px] mx-auto ${ruleColor}`}></div>
        </div>
      </div>
    </section>
  );
}

/** Fallback elegante cuando la combinación todavía no está cargada. */
function MapaNotFound({ dominant, secondary }) {
  const a = (dominant || "—").toString().toUpperCase();
  const b = (secondary || "—").toString().toUpperCase();
  return (
    <div
      className="min-h-screen bg-brandDark text-brandWhite flex flex-col items-center justify-center px-6 text-center mapa-root"
      data-testid="mapa-not-found"
    >
      <span className="text-[10px] tracking-[0.3em] uppercase text-brandGreen font-mono font-bold">
        {MAPA_GLOBAL.notFound.eyebrow}
      </span>
      <h1 className="font-light text-5xl md:text-7xl mt-6">
        {a} <span className="text-brandGreen">+</span> {b}
      </h1>
      <p className="text-brandGrayText mt-6 max-w-md text-sm md:text-base">
        {MAPA_GLOBAL.notFound.body}
      </p>
      <Link
        to="/"
        className="mt-12 text-[11px] tracking-widest uppercase font-mono text-brandGrayText hover:text-brandWhite transition-colors"
      >
        {MAPA_GLOBAL.notFound.back}
      </Link>
    </div>
  );
}
